import React, {
    useCallback, useEffect, useState, useContext
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Icon, Loader } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { TranslatorContext } from '@jutro/locale';
import htmlParser from 'html-react-parser';
import { useDependencies } from 'gw-portals-dependency-react';
import { getFontIcon, getProductName } from '../../../../../utils/lobIconUtil';
import metadata from './AccountDocumentSummaryPage.metadata.json5';
import styles from './AccountDocumentSummaryPage.module.scss';
import AccountDocumentSummary from '../AccountDocumentSummary/AccountDocumentSummary';
import PageBannerComponent
    from '../../../../../../components/PageBannerComponent/PageBannerComponent';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import messages from '../PolicyCommonDocuments.messages.js';

const getPolicyNumber = (document) => {
    const iconName = getFontIcon(document.lob);
    return (
        <div className={styles.accordionTabHeadingWrapper}>
            <Icon icon={iconName} className={styles.headerIcon} title={getProductName(document.lob)} />
            <span className={styles.accordionTabHeading}>
                Policy #&nbsp;
                {document.policyNumber}
            </span>
        </div>
    );
};

function AccountDocumentSummaryPage(props) {
    const { authHeader } = props;
    const { PolicyService } = useDependencies('PolicyService');

    let pageRef = React.createRef();
    const [pageLoaded, setPageLoaded] = useState(false);

    // screen states
    const [isLoading, setLoadingState] = useState(false);
    
    const translator = useContext(TranslatorContext);

    // fetched data
    const [accountDocumentDataFromApi, setAccountDocumentDataFromApi] = useState([]);
    const [billingDocumentDataFromApi, setBillingDocumentDataFromApi] = useState([]);

    const generatePolicyCardOverrides = useCallback(() => {
        const _accountDocumentDataFromApi = accountDocumentDataFromApi.filter((item) => (item.lob !== 'BusinessFleet' && item.lob !== 'GA7Garage' && item.lob !== 'PersonalUmbrella'))
        const overrides = _accountDocumentDataFromApi.map((accountPolicy, index) => {
                return {
                    [`policyNumber${index}`]: {
                        content: getPolicyNumber(accountPolicy)
                    },
                    [`accordionTab${index}`]: {
                        id: `accordionTab${index}`, // accountPolicy.policyNumber,
                        type: 'element',
                        component: 'AccordionCard',
                        header: getPolicyNumber(accountPolicy),
                        content: [
                            {
                                id: `documentContentFor${accountPolicy.policyNumber}`,
                                type: 'container',
                                component: AccountDocumentSummary,
                                componentProps: {
                                    value: accountPolicy,
                                    showFileUpload: false,
                                    layout: ['1', '1', '1', '1']
                                }
                            }
                        ]
                    }
                };
            });

        return Object.assign({}, ...overrides);
    }, [accountDocumentDataFromApi]);

    const generateBillingDocOverrides = useCallback(() => {
        const _billingDocumentDataFromApi = billingDocumentDataFromApi.filter((item) => (item.lob !== 'BusinessFleet' && item.lob !== 'GA7Garage' && item.lob !== 'PersonalUmbrella' && item.latestPackageDocumentDTOs.length >=1))
        const overrides = _billingDocumentDataFromApi.map((billingData, index) => {
                return {
                    [`policyNum${index}`]: {
                        content: getPolicyNumber(billingData)
                    },
                    [`billingAccordionTab${index}`]: {
                        id: `billingAccordionTab${index}`,
                        type: 'element',
                        component: 'AccordionCard',
                        header: getPolicyNumber(billingData),
                        content: [
                            {
                                id: `documentContentFor${billingData.policyNumber}`,
                                type: 'container',
                                component: AccountDocumentSummary,
                                componentProps: {
                                    value: billingData,
                                    showFileUpload: false,
                                    layout: ['1', '1', '1', '1']
                                }
                            }
                        ],
                    }
                };
            });

        return Object.assign({}, ...overrides);
    }, [billingDocumentDataFromApi]);

    useEffect(() => {
        setLoadingState(true);
        // let accountDocumentDataPromise;
        // let billingDocumentDataPromise;
        // accountDocumentDataPromise = PolicyService.getUserDocuments_EY(authHeader).then(setAccountDocumentDataFromApi)
        // billingDocumentDataPromise = AccountBillingDetailsService.getUserBillingDocuments_EY(authHeader).then(setBillingDocumentDataFromApi)
        Promise.all([
            PolicyService.getUserDocuments_EY(authHeader).then(setAccountDocumentDataFromApi),
            AccountBillingDetailsService.getUserBillingDocuments_EY(authHeader).then(setBillingDocumentDataFromApi)
        ]).finally(() => {
            setLoadingState(false);
        });
    }, [setAccountDocumentDataFromApi,setBillingDocumentDataFromApi]);

    useEffect(() => {
        if (!pageLoaded && pageRef.current) {
            const element = ReactDOM.findDOMNode(pageRef.current).parentNode;
            window.setTimeout(() => {
                element.children.docList.focus();
            }, 1000);
            setPageLoaded(true);
        }
    }, [pageRef]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const getBackButton = () => {
        const breakpoint = useContext(BreakpointTrackerContext);
        return breakpoint === 'desktop' ? 'Back' : '';
    };

    const overrideProps = {

        backButton: {
            content: getBackButton()
        },
        agentCompany: {
            content: 'ABC Insurance Brokers Ltd'
        },
        agentName: {
            content: 'MARTHA STEWART, CIP'
        },
        agentAddressLine1: {
            content: '123 Main Street, Ajax, ON, JSM 509'
        },
        agentPhone: {
            content: 'Phone: 905-352-6784'
        },
        agentEmail: {
            content: 'Email: martha.stewart@insurance.com'
        },
        noDataMessageContainer: {
            visible: accountDocumentDataFromApi.length === 0
        },
        policyDocumentsWordings: {
            content: htmlParser(translator(messages.policyWordings))
        },
        repeataccordion: {
            data: accountDocumentDataFromApi ? accountDocumentDataFromApi.filter((item) => (item.lob !== 'BusinessFleet' && item.lob !== 'GA7Garage' && item.lob !== 'PersonalUmbrella')) : [],
            defaultOpenedId: 'accordionTab0',
            accordionStates: ['accordionTab0', 'accordionTab1', 'accordionTab2', 'accordionTab3', 'accordionTab4', 'accordionTab5']
        },
        repeatbillingDocAccordion: {
            data: billingDocumentDataFromApi ? billingDocumentDataFromApi.filter((item) => (item.lob !== 'BusinessFleet' && item.lob !== 'GA7Garage' && item.lob !== 'PersonalUmbrella' && item.latestPackageDocumentDTOs.length >=1)) : [],
            defaultOpenedId: 'billingAccordionTab0',
            accordionStates: ['billingAccordionTab0', 'billingAccordionTab1', 'billingAccordionTab2', 'billingAccordionTab3', 'billingAccordionTab4']
        },
        ...generatePolicyCardOverrides(),
        ...generateBillingDocOverrides(),
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            pagebannercomponent: PageBannerComponent
        }
    };

    return (
        <div>
            <div id="docList" role="navigation" title="Landed Documents Page" tabindex="0" ref={pageRef}></div>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </div>
    );
}

AccountDocumentSummaryPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired
};

export default withAuthenticationContext(withRouter(AccountDocumentSummaryPage));
